import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Button, Input, Modal } from '@react-apps/design-system';
import { useIdentityActions } from '../hooks/use-identity-actions';
import { useProvisionScopedUser } from '../hooks/use-create-user';
import { useScopes } from '@/shared/scopes/use-scopes';
import type { Scope } from '@/shared/scopes/scopes-api.datasource';
import {
  accessGrantedMessage,
  accessGrantErrorMessage,
  maskEmail,
} from '../access-grant-outcome';
import { ScopeSelect } from './ScopeSelect';

interface CreatePlatformProfileModalProps {
  userId: string;
  userName: string;
  userEmail: string;
  open: boolean;
  onClose: () => void;
  /** Presente = modo iamEditor; ver o mesmo prop no `CreateUserModal`. */
  provisionableScopes?: UseQueryResult<Scope[]>;
}

/**
 * Dá à pessoa acesso a um novo scope (plataforma).
 *
 * - **Admin:** cria o perfil pela rota dele, com o nome que escolher; o back
 *   desativa um anterior do mesmo scope, se houver.
 * - **iamEditor:** só nas plataformas que administra, pela rota única, sem
 *   nome (a conta existe, e o back não altera a pessoa). Perfil ativo é
 *   reaproveitado; inativo, só o admin reativa.
 */
export function CreatePlatformProfileModal({
  userId,
  userName,
  userEmail,
  open,
  onClose,
  provisionableScopes,
}: CreatePlatformProfileModalProps) {
  const scoped = provisionableScopes !== undefined;
  const [name, setName] = useState(userName);
  const [scopeId, setScopeId] = useState('');
  const admin = useIdentityActions(userId).createPlatformProfile;
  const iamEditor = useProvisionScopedUser();
  const allScopes = useScopes(!scoped);
  const mutation = scoped ? iamEditor : admin;
  const scopeSource = provisionableScopes ?? allScopes;

  const close = () => {
    admin.reset();
    iamEditor.reset();
    setName(userName);
    setScopeId('');
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (scoped) iamEditor.mutate({ email: userEmail, scopeId });
    else admin.mutate({ userId, name: name.trim(), scopeId });
  };

  const canSubmit =
    (scoped || name.trim().length > 0) &&
    scopeId.length > 0 &&
    !scopeSource.isError &&
    !mutation.isPending;

  // A rota do admin não diz se reaproveitou: ela sempre cria.
  const successMessage = accessGrantedMessage(
    scoped ? (iamEditor.data?.profileCreated ?? true) : true,
  );

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        scoped ? 'Dar acesso a uma plataforma' : 'Criar perfil de plataforma'
      }
      description={
        scoped
          ? `Para ${maskEmail(userEmail)}. Você só pode dar acesso às plataformas que administra.`
          : 'Dá ao usuário acesso a um novo scope (plataforma). O perfil nasce ativo.'
      }
    >
      {mutation.isSuccess ? (
        <div className="space-y-4">
          <p role="status" className="text-(--color-text-secondary) text-sm">
            {successMessage}
          </p>
          <div className="flex justify-end pt-2">
            <Button type="button" onClick={close}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <ScopeSelect
            label={scoped ? 'Plataforma' : 'Scope'}
            value={scopeId}
            onChange={setScopeId}
            source={scopeSource}
            emptyPlaceholder={
              scoped ? 'Selecione uma plataforma…' : 'Selecione um scope…'
            }
          />
          {!scoped && (
            <Input
              label="Nome do perfil"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
          {mutation.error && (
            <p role="alert" className="text-sm text-red-600">
              {accessGrantErrorMessage(mutation.error)}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={close}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? 'Criando…' : 'Criar'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
